import { FC } from "react"
import classNames from "classnames"
import { SearchAndSelect } from "@/components/SearchAndSelect"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import { Icon, IconColor } from "@/components/shared/Icon"
import { useJmsEnabled } from "@/hooks/useJmsEnabled"
import { Inmate } from "@/types/inmate"
import { formatInmateName, getFullInmateInfo } from "@/utils/index"
import { getBatteryChargeIcon } from "@/utils/index"
import "./index.scss"

interface WearableInmateSelectionProps {
   inmates?: Inmate[]
   wearableId?: string
   batteryPercentage?: number | null
   isLoading?: boolean
   onCancel: () => void
   onAddNewInmate: () => void
   onSave: (inmateId: string) => void
}

const WearableInmateSelection: FC<WearableInmateSelectionProps> = ({
   inmates = [],
   wearableId,
   batteryPercentage,
   isLoading = false,
   onCancel,
   onSave,
   onAddNewInmate,
}) => {
   const jmsEnabled = useJmsEnabled()

   const filterInmates = (inmate: Inmate, searchTerm: string) => {
      const { customIdentifier, bookingNumber } = inmate
      const fullInmateName = formatInmateName(inmate)
      const searchValue = searchTerm.toLowerCase()
      return (
         customIdentifier?.toLowerCase().includes(searchValue) ||
         fullInmateName.toLowerCase().includes(searchValue) ||
         bookingNumber?.includes(searchValue) ||
         false
      )
   }

   const renderInmate = (
      inmate: Inmate,
      isSelected: boolean,
      handleSelect: (inmate: Inmate) => void
   ) => (
      <div
         key={inmate.id}
         className={classNames("wearable-inmate-selection__item", {
            "wearable-inmate-selection__item--selected": isSelected,
         })}
         onClick={() => handleSelect(inmate)}
      >
         <div className="wearable-inmate-selection__item-name">
            {inmate?.customIdentifier || getFullInmateInfo(inmate)}
         </div>
      </div>
   )

   const title = (
      <>
         Please select an inmate from the list to connect it to wearable&nbsp;
         {wearableId && (
            <span className="wearable-inmate-selection__wearable-id">
               {wearableId}
            </span>
         )}
         {batteryPercentage != null && (
            <span className="wearable-inmate-selection__battery">
               <Icon
                  name={getBatteryChargeIcon(batteryPercentage)}
                  color={IconColor.HONOLULU_BLUE}
                  size="16"
               />
               <span>{batteryPercentage}%</span>
            </span>
         )}
      </>
   )

   return (
      <div className="wearable-inmate-selection__container">
         <SearchAndSelect
            items={inmates}
            title={title}
            noResultsTitleTerm="inmates"
            removePadding
            isLoading={isLoading}
            onCancel={onCancel}
            onSave={onSave}
            filter={filterInmates}
            renderItem={renderInmate}
         />
         {!jmsEnabled && (
            <div className="wearable-inmate-selection__new-inmate">
               <span className="wearable-inmate-selection__new-inmate-text">
                  Or add a new inmate to connect it to
               </span>
               <Button
                  iconName="plus"
                  iconPosition={ButtonIconPosition.LEFT}
                  onClick={onAddNewInmate}
               >
                  Add new inmate
               </Button>
            </div>
         )}
      </div>
   )
}

export default WearableInmateSelection
